import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { assetUrl, fetchJSON } from '../../utils/api';
import { addItemToCart, loadMerchCart, saveMerchCart } from '../../utils/merchCart';
import MerchandiseCartModal from './MerchandiseCartModal';
import MerchandiseStockRequestModal from './MerchandiseStockRequestModal';
import './MerchandiseGallery2D.css';

const GALLERY_PRESS_ZOOM_TRANSITION_MS = 700;
const GALLERY_HOLD_TO_ZOOM_DELAY_MS = 110;
const GALLERY_HOLD_ZOOM_BASE_SCALE = 2.8;
const GALLERY_HOLD_ZOOM_OFFSET_MIN = -1.8;
const GALLERY_HOLD_ZOOM_OFFSET_DEFAULT_MAX = 1.5;
const GALLERY_HOLD_ZOOM_OFFSET_HI_RES_MAX = 6;
const GALLERY_HOLD_ZOOM_OFFSET_STEP = 0.1;
const GALLERY_PINCH_ZOOM_MIN = 1;
const GALLERY_PINCH_ZOOM_MAX = 5;
const GALLERY_MOBILE_SWIPE_MIN_DISTANCE_PX = 44;
const GALLERY_RELEASE_WAVE_MS = 1000;

function formatNok(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return 'Pris kommer';
  return `${n.toLocaleString('nb-NO')} NOK`;
}

function clampIndex(index, length) {
  if (!Number.isFinite(index) || length <= 0) return 0;
  if (index < 0) return 0;
  if (index > length - 1) return length - 1;
  return index;
}

function clampZoomScale(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return GALLERY_PINCH_ZOOM_MIN;
  return Math.min(GALLERY_PINCH_ZOOM_MAX, Math.max(GALLERY_PINCH_ZOOM_MIN, numeric));
}

function clampHoldZoomOffset(value, maxOffset = GALLERY_HOLD_ZOOM_OFFSET_DEFAULT_MAX) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.min(maxOffset, Math.max(GALLERY_HOLD_ZOOM_OFFSET_MIN, numeric));
}

function resolveTouchDistance(touchA, touchB) {
  if (!touchA || !touchB) return 0;
  const dx = Number(touchA.clientX) - Number(touchB.clientX);
  const dy = Number(touchA.clientY) - Number(touchB.clientY);
  if (!Number.isFinite(dx) || !Number.isFinite(dy)) return 0;
  return Math.hypot(dx, dy);
}

function normalizeMerchImageEntry(item, index) {
  const rawMain = String(item?.optimizedUrl || item?.url || item?.originalUrl || item?.thumbUrl || '').trim();
  const rawHigh = String(item?.originalUrl || item?.storageUrl || rawMain || '').trim();
  const mainSrc = rawMain ? assetUrl(rawMain) : '';
  const highSrc = rawHigh ? assetUrl(rawHigh) : mainSrc;
  return {
    id: String(item?._id || item?.id || `merch-image-${index + 1}`),
    title: String(item?.title || item?.subject || `Bilde ${index + 1}`).trim() || `Bilde ${index + 1}`,
    description: String(item?.description || item?.text || '').trim(),
    mainSrc,
    highSrc,
    thumbSrc: mainSrc,
    order: Number(item?.order),
  };
}

function createInfoCard(title, description) {
  return {
    id: 'merch-card-info',
    type: 'info',
    title: String(title || '').trim() || 'Information',
    description: String(description || '').trim(),
    mainSrc: '',
    highSrc: '',
    thumbSrc: '',
  };
}

function createInventoryCard(stockCount, variantGroups, variantStock) {
  return {
    id: 'merch-card-inventory',
    type: 'inventory',
    title: 'INVENTORY STATUS',
    description: '',
    stockCount,
    variantGroups,
    variantStock,
    mainSrc: '',
    highSrc: '',
    thumbSrc: '',
  };
}

function getVariantAttributeValue(row, title) {
  const attrs = Array.isArray(row?.attributes) ? row.attributes : [];
  const hit = attrs.find((entry) => String(entry?.title || '').trim() === title);
  return String(hit?.value || '').trim();
}

function normalizeVariantGroups(input) {
  return (Array.isArray(input) ? input : [])
    .map((group) => ({
      title: String(group?.title || '').trim(),
      options: Array.isArray(group?.options)
        ? group.options.map((value) => String(value || '').trim()).filter(Boolean)
        : [],
    }))
    .filter((group) => group.title && group.options.length);
}

function getAvailableStockCount(row) {
  return Math.max(0, (Number(row?.stockOnHand) || 0) - (Number(row?.reservedCount) || 0));
}

function deriveRequestedSizeFromSelectedVariants(selectedVariants) {
  const rows = Array.isArray(selectedVariants) ? selectedVariants : [];
  const sizeRow = rows.find((entry) => /size|stoerrelse|størrelse/i.test(String(entry?.title || '')));
  return sizeRow ? String(sizeRow.value || '').trim().toUpperCase() : '';
}

function buildSelectedVariantsForInventoryCell(variantGroups, rowLabel, columnLabel) {
  const groups = normalizeVariantGroups(variantGroups);
  if (!groups.length) return [];

  const selections = [];
  if (rowLabel) {
    selections.push({ title: groups[0].title, value: String(rowLabel || '').trim() });
  }
  if (columnLabel && groups[1]) {
    selections.push({ title: groups[1].title, value: String(columnLabel || '').trim() });
  }
  return selections.filter((entry) => entry.title && entry.value);
}

function getInventoryActionState(stockCount) {
  if (stockCount > 10) {
    return {
      action: 'cart',
      label: 'ADD TO CART',
      borderColor: '#83ffc3',
      textColor: '#effff5',
      background: 'rgba(10, 54, 27, 0.82)',
    };
  }
  if (stockCount > 0) {
    return {
      action: 'cart',
      label: 'ADD TO CART',
      borderColor: '#ffd667',
      textColor: '#fff6d6',
      background: 'rgba(84, 63, 10, 0.82)',
    };
  }
  return {
    action: 'request',
    label: 'OUT OF STOCK',
    borderColor: '#ff9999',
    textColor: '#ffe2e2',
    background: 'rgba(78, 21, 21, 0.82)',
  };
}

function buildInventoryTableData(variantGroups, variantStock) {
  const groups = Array.isArray(variantGroups) ? variantGroups : [];
  const rows = Array.isArray(variantStock) ? variantStock : [];
  const firstGroup = groups[0] || null;
  const secondGroup = groups[1] || null;

  if (!firstGroup) {
    return {
      rowHeader: 'Variant',
      columnHeader: 'Stock',
      rowLabels: rows.map((row, index) => String(row?.label || `Variant ${index + 1}`)),
      columnLabels: ['Stock'],
      lookup: Object.fromEntries(
        rows.map((row, index) => {
          const label = String(row?.label || `Variant ${index + 1}`);
          const stock = Math.max(0, (Number(row?.stockOnHand) || 0) - (Number(row?.reservedCount) || 0));
          return [`${label}::Stock`, stock];
        })
      ),
    };
  }

  const rowLabels = Array.isArray(firstGroup?.options)
    ? firstGroup.options.map((value) => String(value || '').trim()).filter(Boolean)
    : [];

  if (!secondGroup) {
    const lookup = {};
    rowLabels.forEach((rowLabel) => {
      const stockRow = rows.find((row) => getVariantAttributeValue(row, String(firstGroup.title || '')) === rowLabel);
      lookup[`${rowLabel}::Stock`] = Math.max(
        0,
        (Number(stockRow?.stockOnHand) || 0) - (Number(stockRow?.reservedCount) || 0)
      );
    });
    return {
      rowHeader: String(firstGroup.title || 'Variant 1'),
      columnHeader: 'Stock',
      rowLabels,
      columnLabels: ['Stock'],
      lookup,
    };
  }

  const columnLabels = Array.isArray(secondGroup?.options)
    ? secondGroup.options.map((value) => String(value || '').trim()).filter(Boolean)
    : [];

  const lookup = {};
  rowLabels.forEach((rowLabel) => {
    columnLabels.forEach((columnLabel) => {
      const stockRow = rows.find((row) => (
        getVariantAttributeValue(row, String(firstGroup.title || '')) === rowLabel
        && getVariantAttributeValue(row, String(secondGroup.title || '')) === columnLabel
      ));
      lookup[`${rowLabel}::${columnLabel}`] = Math.max(
        0,
        (Number(stockRow?.stockOnHand) || 0) - (Number(stockRow?.reservedCount) || 0)
      );
    });
  });

  return {
    rowHeader: String(firstGroup.title || 'Variant 1'),
    columnHeader: String(secondGroup.title || 'Variant 2'),
    rowLabels,
    columnLabels,
    lookup,
  };
}

const EMPTY_STOCK_REQUEST_DRAFT = {
  open: false,
  selectedVariants: [],
  requestKey: '',
};

const MerchandiseGallery2D = () => {
  const navigate = useNavigate();
  const { galleryId } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [meta, setMeta] = useState(null);
  const [images, setImages] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState('next');
  const [transitionToken, setTransitionToken] = useState(0);
  const [imageReadyImageId, setImageReadyImageId] = useState('');
  const [cartItems, setCartItems] = useState(() => loadMerchCart());
  const [showCart, setShowCart] = useState(false);
  const [showBuyEntry, setShowBuyEntry] = useState(false);
  const [cartFeedback, setCartFeedback] = useState('');
  const [inventoryActionPendingKey, setInventoryActionPendingKey] = useState('');
  const [stockRequestDraft, setStockRequestDraft] = useState(EMPTY_STOCK_REQUEST_DRAFT);
  const [holdZoomOffset, setHoldZoomOffset] = useState(0);
  const [hiResEnabled, setHiResEnabled] = useState(false);
  const [pressZoom, setPressZoom] = useState({ active: false, mediaId: '', x: 50, y: 50, scale: 1 });
  const [zoomRipples, setZoomRipples] = useState([]);
  const touchHoldTimerRef = useRef(null);
  const pressZoomRef = useRef(pressZoom);
  const rippleIdRef = useRef(0);
  const rippleTimeoutsRef = useRef([]);
  const touchGestureRef = useRef({
    startX: 0,
    startY: 0,
    startedAt: 0,
    holdActive: false,
    pinchBaseDistance: 0,
    pinchBaseScale: 1,
  });

  const activeImage = useMemo(() => {
    if (!images.length) return null;
    const idx = clampIndex(activeIndex, images.length);
    return images[idx] || null;
  }, [images, activeIndex]);

  const activeImageIndexDisplay = useMemo(() => {
    if (!images.length) return 0;
    return clampIndex(activeIndex, images.length) + 1;
  }, [activeIndex, images.length]);

  const totalCartItems = useMemo(
    () => (Array.isArray(cartItems) ? cartItems.reduce((sum, item) => sum + Math.max(1, Number(item?.quantity) || 1), 0) : 0),
    [cartItems]
  );

  const zoomActiveForCurrentImage = useMemo(
    () => !!activeImage?.id && pressZoom.active && pressZoom.mediaId === activeImage.id,
    [activeImage?.id, pressZoom.active, pressZoom.mediaId]
  );

  const mediaZoomTransform = zoomActiveForCurrentImage ? `scale(${Math.max(1, pressZoom.scale || 1)})` : 'scale(1)';
  const mediaZoomOrigin = `${(activeImage?.id && pressZoom.mediaId === activeImage.id) ? pressZoom.x : 50}% ${(activeImage?.id && pressZoom.mediaId === activeImage.id) ? pressZoom.y : 50}%`;
  const holdZoomOffsetMax = hiResEnabled ? GALLERY_HOLD_ZOOM_OFFSET_HI_RES_MAX : GALLERY_HOLD_ZOOM_OFFSET_DEFAULT_MAX;
  const holdZoomScale = clampZoomScale(GALLERY_HOLD_ZOOM_BASE_SCALE + holdZoomOffset);
  const hiResAvailable = !!activeImage?.highSrc && activeImage.highSrc !== activeImage.mainSrc;
  const effectiveHiResEnabled = hiResEnabled && hiResAvailable;
  const activeImageDisplaySrc = effectiveHiResEnabled ? activeImage?.highSrc : activeImage?.mainSrc;
  const buyEntryVariantGroups = useMemo(() => normalizeVariantGroups(meta?.variantGroups), [meta?.variantGroups]);
  const buyEntryInventoryTable = useMemo(
    () => buildInventoryTableData(buyEntryVariantGroups, meta?.variantStock),
    [buyEntryVariantGroups, meta?.variantStock]
  );

  useEffect(() => {
    pressZoomRef.current = pressZoom;
  }, [pressZoom]);

  const moveTo = useCallback((nextIndex) => {
    if (!images.length) return;
    const current = clampIndex(activeIndex, images.length);
    const target = clampIndex(nextIndex, images.length);
    if (target === current) return;

    setDirection(target > current ? 'next' : 'prev');
    setActiveIndex(target);
    setTransitionToken((prev) => prev + 1);
  }, [activeIndex, images.length]);

  const moveBy = useCallback((delta) => {
    if (!images.length) return;
    const current = clampIndex(activeIndex, images.length);
    const dir = delta < 0 ? -1 : 1;
    const target = (current + dir + images.length) % images.length;
    setDirection(dir > 0 ? 'next' : 'prev');
    setActiveIndex(target);
    setTransitionToken((prev) => prev + 1);
  }, [activeIndex, images.length]);

  const clearTouchHoldTimer = useCallback(() => {
    if (touchHoldTimerRef.current != null) {
      window.clearTimeout(touchHoldTimerRef.current);
      touchHoldTimerRef.current = null;
    }
    touchGestureRef.current.holdActive = false;
  }, []);

  const scheduleZoomRipple = useCallback((snapshot) => {
    if (!snapshot?.mediaId) return;
    const id = rippleIdRef.current + 1;
    rippleIdRef.current = id;

    const ripple = {
      id,
      mediaId: snapshot.mediaId,
      x: Number.isFinite(snapshot.x) ? snapshot.x : 50,
      y: Number.isFinite(snapshot.y) ? snapshot.y : 50,
    };

    setZoomRipples((prev) => [...prev.slice(-4), ripple]);

    const timeoutId = window.setTimeout(() => {
      setZoomRipples((prev) => prev.filter((entry) => entry.id !== id));
    }, GALLERY_RELEASE_WAVE_MS);

    rippleTimeoutsRef.current.push(timeoutId);
  }, []);

  const releasePressZoom = useCallback(() => {
    clearTouchHoldTimer();

    const snapshot = pressZoomRef.current;
    if (snapshot?.active && snapshot.mediaId) {
      scheduleZoomRipple(snapshot);
    }

    setPressZoom((prev) => {
      if (!prev.active && prev.scale === 1) return prev;
      if (!prev.mediaId) {
        return { active: false, mediaId: '', x: 50, y: 50, scale: 1 };
      }
      return {
        ...prev,
        active: false,
        scale: 1,
      };
    });
  }, [clearTouchHoldTimer, scheduleZoomRipple]);

  const updatePressZoom = useCallback((event, mediaId, activate = false, options = {}) => {
    if (!mediaId) return;
    const element = event.currentTarget;
    if (!element) return;

    const point = event.touches?.[0] || event.changedTouches?.[0] || event;
    const clientX = Number(point?.clientX);
    const clientY = Number(point?.clientY);
    if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) return;

    const rect = element.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const x = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((clientY - rect.top) / rect.height) * 100));

    setPressZoom((prev) => {
      if (!activate && (!prev.active || prev.mediaId !== mediaId)) {
        return prev;
      }

      const defaultScale = activate
        ? ((prev.active && prev.mediaId === mediaId) ? prev.scale : holdZoomScale)
        : prev.scale;
      const nextScale = clampZoomScale(options.scale ?? defaultScale);

      return {
        active: true,
        mediaId,
        x,
        y,
        scale: nextScale,
      };
    });
  }, [holdZoomScale]);

  const getPressZoomHandlers = useCallback((mediaId) => ({
    onMouseDown: (event) => updatePressZoom(event, mediaId, true, { scale: holdZoomScale }),
    onMouseMove: (event) => updatePressZoom(event, mediaId, false),
    onMouseUp: releasePressZoom,
    onMouseLeave: releasePressZoom,
    onTouchStart: (event) => {
      const element = event.currentTarget;
      if (!element) return;

      const touches = event.touches;
      if (touches.length >= 2) {
        clearTouchHoldTimer();
        const touchA = touches[0];
        const touchB = touches[1];
        const centerX = (Number(touchA.clientX) + Number(touchB.clientX)) / 2;
        const centerY = (Number(touchA.clientY) + Number(touchB.clientY)) / 2;
        const pinchDistance = resolveTouchDistance(touchA, touchB);
        const baseScale = pressZoom.active && pressZoom.mediaId === mediaId
          ? pressZoom.scale
          : 1.6;

        touchGestureRef.current = {
          startX: centerX,
          startY: centerY,
          startedAt: Date.now(),
          holdActive: true,
          pinchBaseDistance: Math.max(1, pinchDistance),
          pinchBaseScale: clampZoomScale(baseScale),
        };

        updatePressZoom(
          { currentTarget: element, touches: [{ clientX: centerX, clientY: centerY }] },
          mediaId,
          true,
          { scale: clampZoomScale(baseScale) }
        );
        return;
      }

      const touch = touches[0];
      if (!touch) return;
      const startX = Number(touch.clientX);
      const startY = Number(touch.clientY);

      touchGestureRef.current = {
        startX,
        startY,
        startedAt: Date.now(),
        holdActive: false,
        pinchBaseDistance: 0,
        pinchBaseScale: 1,
      };

      clearTouchHoldTimer();
      touchHoldTimerRef.current = window.setTimeout(() => {
        touchGestureRef.current.holdActive = true;
        updatePressZoom(
          { currentTarget: element, touches: [{ clientX: startX, clientY: startY }] },
          mediaId,
          true,
          { scale: holdZoomScale }
        );
        touchHoldTimerRef.current = null;
      }, GALLERY_HOLD_TO_ZOOM_DELAY_MS);
    },
    onTouchMove: (event) => {
      const element = event.currentTarget;
      if (!element) return;

      const touches = event.touches;
      if (touches.length >= 2) {
        clearTouchHoldTimer();
        const touchA = touches[0];
        const touchB = touches[1];
        const centerX = (Number(touchA.clientX) + Number(touchB.clientX)) / 2;
        const centerY = (Number(touchA.clientY) + Number(touchB.clientY)) / 2;
        const pinchDistance = resolveTouchDistance(touchA, touchB);
        const baseDistance = Math.max(1, touchGestureRef.current.pinchBaseDistance || pinchDistance);
        const baseScale = clampZoomScale(touchGestureRef.current.pinchBaseScale || 1.6);
        const scaleRatio = pinchDistance > 0 ? pinchDistance / baseDistance : 1;
        const scale = clampZoomScale(baseScale * scaleRatio);

        touchGestureRef.current.holdActive = true;

        updatePressZoom(
          { currentTarget: element, touches: [{ clientX: centerX, clientY: centerY }] },
          mediaId,
          true,
          { scale }
        );
        event.preventDefault();
        return;
      }

      const touch = touches[0];
      if (!touch) return;

      const dx = Number(touch.clientX) - touchGestureRef.current.startX;
      const dy = Number(touch.clientY) - touchGestureRef.current.startY;
      if (!touchGestureRef.current.holdActive && (Math.abs(dx) > 14 || Math.abs(dy) > 14)) {
        clearTouchHoldTimer();
      }

      if (pressZoom.active && pressZoom.mediaId === mediaId) {
        updatePressZoom(event, mediaId, false);
        event.preventDefault();
      }
    },
    onTouchEnd: (event) => {
      const gesture = touchGestureRef.current;
      const changedTouch = event.changedTouches?.[0];

      if (!gesture.holdActive && changedTouch && images.length > 1) {
        const dx = Number(changedTouch.clientX) - gesture.startX;
        const dy = Number(changedTouch.clientY) - gesture.startY;
        const elapsed = Date.now() - gesture.startedAt;
        const isSwipe = Math.abs(dx) >= GALLERY_MOBILE_SWIPE_MIN_DISTANCE_PX
          && Math.abs(dx) > (Math.abs(dy) * 1.1)
          && elapsed < 700;

        if (isSwipe) {
          moveBy(dx < 0 ? 1 : -1);
        }
      }

      clearTouchHoldTimer();
      releasePressZoom();
    },
    onTouchCancel: () => {
      clearTouchHoldTimer();
      releasePressZoom();
    },
    onContextMenu: (event) => event.preventDefault(),
  }), [
    clearTouchHoldTimer,
    holdZoomScale,
    images.length,
    moveBy,
    pressZoom.active,
    pressZoom.mediaId,
    pressZoom.scale,
    releasePressZoom,
    updatePressZoom,
  ]);

  const handleBuyClick = useCallback(() => {
    if (!meta?.id) return;
    setShowBuyEntry(true);
  }, [meta?.id]);

  const addInventorySelectionToCart = useCallback((selectedVariants) => {
    if (!meta?.id) return;

    const nextCart = addItemToCart(cartItems, {
      productId: String(meta.id),
      itemTitle: String(meta.title || 'Merchandise').trim(),
      itemImageSrc: String(meta.imageSrc || '').trim(),
      saleType: 'Other',
      salePriceNok: Number(meta.salePriceNok || 0) || 0,
      requestedSize: deriveRequestedSizeFromSelectedVariants(selectedVariants),
      selectedVariants: Array.isArray(selectedVariants) ? selectedVariants : [],
      variantGroups: normalizeVariantGroups(meta.variantGroups),
      quantity: 1,
    });

    setCartItems(nextCart);
    saveMerchCart(nextCart);
    setShowCart(true);
    setCartFeedback('Added to DØDSEGO CART');
  }, [cartItems, meta]);

  const openStockRequestModal = useCallback((selectedVariants, requestKey) => {
    const nextKey = String(requestKey || 'inventory-request').trim() || 'inventory-request';
    setStockRequestDraft({
      open: true,
      selectedVariants: Array.isArray(selectedVariants) ? selectedVariants : [],
      requestKey: nextKey,
    });
  }, []);

  const requestInventorySelection = useCallback(async (selectedVariants, requestKey, requestDetails = {}) => {
    if (!meta?.id) return false;

    const nextKey = String(requestKey || 'inventory-request').trim() || 'inventory-request';
    setInventoryActionPendingKey(nextKey);
    try {
      await fetchJSON(`/api/merchandise/products/${encodeURIComponent(String(meta.id))}/public-stock-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          selectedVariants: Array.isArray(selectedVariants) ? selectedVariants : [],
          requestedSize: deriveRequestedSizeFromSelectedVariants(selectedVariants),
          customerName: String(requestDetails?.customerName || '').trim(),
          customerEmail: String(requestDetails?.customerEmail || '').trim(),
          requestText: String(requestDetails?.requestText || '').trim(),
        }),
      });
      setCartFeedback('Stock request sent');
      return true;
    } catch (requestError) {
      const text = String(requestError?.message || '');
      if (text.includes('stock_available')) {
        setCartFeedback('This combination is back in stock. Add it to cart.');
      } else if (text.includes('no_assignees')) {
        setCartFeedback('No assignee is configured for stock requests.');
      } else if (
        text.includes('invalid_customer_name')
        || text.includes('invalid_customer_email')
        || text.includes('invalid_request_text')
      ) {
        setCartFeedback('Please fill in all request fields.');
      } else {
        setCartFeedback('Could not send stock request.');
      }
      return false;
    } finally {
      setInventoryActionPendingKey('');
    }
  }, [meta?.id]);

  const submitStockRequestFromModal = useCallback(async ({ customerName, customerEmail, requestText }) => {
    if (!meta?.id) {
      return { ok: false, message: 'Could not resolve product.' };
    }

    const ok = await requestInventorySelection(
      stockRequestDraft.selectedVariants,
      stockRequestDraft.requestKey,
      { customerName, customerEmail, requestText }
    );

    if (ok) {
      setStockRequestDraft(EMPTY_STOCK_REQUEST_DRAFT);
      setShowBuyEntry(false);
    }

    return ok
      ? { ok: true }
      : { ok: false, message: 'Could not send stock request.' };
  }, [meta?.id, requestInventorySelection, stockRequestDraft.requestKey, stockRequestDraft.selectedVariants]);

  useEffect(() => {
    if (!activeImage?.id) {
      setImageReadyImageId('');
      return;
    }

    setImageReadyImageId('');
  }, [activeImage?.id, activeImageDisplaySrc]);

  useEffect(() => {
    setHoldZoomOffset((prev) => clampHoldZoomOffset(prev, holdZoomOffsetMax));
  }, [holdZoomOffsetMax]);

  useEffect(() => {
    saveMerchCart(cartItems);
  }, [cartItems]);

  useEffect(() => {
    if (!cartFeedback) return undefined;
    const timerId = window.setTimeout(() => {
      setCartFeedback('');
    }, 1700);
    return () => {
      window.clearTimeout(timerId);
    };
  }, [cartFeedback]);

  useEffect(() => {
    if (!pressZoom.active) return () => {};

    window.addEventListener('mouseup', releasePressZoom);
    window.addEventListener('touchend', releasePressZoom);
    window.addEventListener('touchcancel', releasePressZoom);
    window.addEventListener('blur', releasePressZoom);

    return () => {
      window.removeEventListener('mouseup', releasePressZoom);
      window.removeEventListener('touchend', releasePressZoom);
      window.removeEventListener('touchcancel', releasePressZoom);
      window.removeEventListener('blur', releasePressZoom);
    };
  }, [pressZoom.active, releasePressZoom]);

  useEffect(() => {
    clearTouchHoldTimer();
    setPressZoom({ active: false, mediaId: '', x: 50, y: 50, scale: 1 });
    setZoomRipples([]);
  }, [activeImage?.id, clearTouchHoldTimer]);

  useEffect(() => () => {
    if (touchHoldTimerRef.current != null) {
      window.clearTimeout(touchHoldTimerRef.current);
      touchHoldTimerRef.current = null;
    }

    rippleTimeoutsRef.current.forEach((timerId) => {
      window.clearTimeout(timerId);
    });
    rippleTimeoutsRef.current = [];
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (!images.length) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        moveBy(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        moveBy(1);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [images.length, moveBy]);

  useEffect(() => {
    if (!galleryId) {
      setLoading(false);
      setError('Ingen merchandise-ID ble funnet.');
      return;
    }

    let active = true;
    (async () => {
      setLoading(true);
      setError('');

      try {
        const id = decodeURIComponent(galleryId);
        const data = await fetchJSON(`/api/merchandise/products/${encodeURIComponent(id)}/public-gallery`, { cache: 'no-store' });

        if (!active) return;

        const showInfo = true;
        const showInventory = true;

        const normalizedImages = (Array.isArray(data?.items) ? data.items : [])
          .slice()
          .sort((a, b) => {
            const left = Number(a?.order);
            const right = Number(b?.order);
            if (Number.isFinite(left) && Number.isFinite(right)) return left - right;
            if (Number.isFinite(left)) return -1;
            if (Number.isFinite(right)) return 1;
            return 0;
          })
          .map((item, index) => normalizeMerchImageEntry(item, index))
          .filter((item) => item.mainSrc);

        const availableStock = Number.isFinite(Number(data?.totalAvailableStock))
          ? Math.max(0, Math.round(Number(data?.totalAvailableStock)))
          : 0;

        const title = String(data?.title || 'Merchandise').trim() || 'Merchandise';
        const description = String(data?.saleText || '').trim();
        const variantGroups = Array.isArray(data?.variantGroups) ? data.variantGroups : [];

        // Build final images array with info and inventory cards
        let finalImages = [...normalizedImages];
        if (showInfo) {
          finalImages.unshift(createInfoCard(title, description));
        }
        if (showInventory) {
          finalImages.push(createInventoryCard(availableStock, variantGroups, data?.variantStock));
        }

        setMeta({
          id: String(data?.id || id),
          title,
          description,
          blurb: String(data?.blurb || '').trim(),
          imageSrc: String(normalizedImages[0]?.mainSrc || '').trim(),
          salePriceNok: Number(data?.salePriceNok || 0) || 0,
          shippingCostNok: Math.max(0, Number(data?.shippingCostNok || 75) || 75),
          shippingCostInternationalNok: Math.max(0, Number(data?.shippingCostInternationalNok || 150) || 150),
          totalAvailableStock: availableStock,
          variantGroups,
          variantStock: Array.isArray(data?.variantStock) ? data.variantStock : [],
        });

        setImages(finalImages);
        setActiveIndex(0);

        if (!normalizedImages.length) {
          setError('Dette merchandise-elementet har ingen bilder ennå.');
        }
      } catch (loadError) {
        if (!active) return;
        setError(String(loadError?.message || 'Kunne ikke hente merchandise-bilder.'));
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [galleryId]);

  const availabilityLabel = (meta?.totalAvailableStock || 0) > 0
    ? 'IN STOCK'
    : 'OUT OF STOCK, LONG DELIVERY TIME';

  const availabilityClass = (meta?.totalAvailableStock || 0) > 0
    ? 'merch-gallery2d-stock merch-gallery2d-stock--in'
    : 'merch-gallery2d-stock merch-gallery2d-stock--out';

  return (
    <div className="merch-gallery2d-root">
      <div className="merch-gallery2d-atmosphere" aria-hidden />
      <div className="merch-gallery2d-orb merch-gallery2d-orb--left" aria-hidden />
      <div className="merch-gallery2d-orb merch-gallery2d-orb--right" aria-hidden />

      <div className="merch-gallery2d-wrap">
        <header className="merch-gallery2d-header">
          <button
            type="button"
            className="merch-gallery2d-back"
            onClick={() => navigate('/merchandise')}
          >
            BACK
          </button>

          <div className="merch-gallery2d-meta">
            <div className="merch-gallery2d-kicker">DØDSEGO MERCH LOOKBOOK</div>
            <h1>{meta?.title || 'Merchandise'}</h1>
            {String(meta?.blurb || '').trim() ? (
              <div style={{ fontSize: '150%', lineHeight: 1.25, textAlign: 'left', marginBottom: 10, fontWeight: 600 }}>
                {String(meta?.blurb || '').trim()}
              </div>
            ) : null}
            <div className="merch-gallery2d-meta-row">
              <span className="merch-gallery2d-price">{formatNok(meta?.salePriceNok)}</span>
              <button
                type="button"
                className="merch-gallery2d-buy"
                onClick={handleBuyClick}
                disabled={!meta}
                title={Array.isArray(meta?.variantGroups) && meta.variantGroups.length > 0
                  ? 'Select variant and purchase'
                  : 'Purchase'}
              >
                PURCHASE
              </button>
              <span className={availabilityClass}>{availabilityLabel}</span>
              <label
                className="merch-gallery2d-zoom-slider-wrap"
                title={`Offset for click-and-hold zoom. Current hold zoom ${holdZoomScale.toFixed(1)}x`}
              >
                <span className="merch-gallery2d-zoom-slider-label">HOLD ZOOM</span>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', alignItems: 'center', gap: 10 }}>
                  <input
                    type="range"
                    className="merch-gallery2d-zoom-slider"
                    min={GALLERY_HOLD_ZOOM_OFFSET_MIN}
                    max={holdZoomOffsetMax}
                    step={GALLERY_HOLD_ZOOM_OFFSET_STEP}
                    value={holdZoomOffset}
                    onChange={(event) => setHoldZoomOffset(clampHoldZoomOffset(Number(event.target.value), holdZoomOffsetMax))}
                    aria-label="Click and hold zoom offset"
                  />
                  <span className="merch-gallery2d-zoom-slider-value">
                    {holdZoomOffset >= 0 ? '+' : ''}{holdZoomOffset.toFixed(1)}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      if (!hiResAvailable) return;
                      setHiResEnabled((prev) => !prev);
                    }}
                    disabled={!hiResAvailable}
                    style={{
                      borderRadius: 9,
                      border: `1px solid ${effectiveHiResEnabled ? 'rgba(255,232,163,0.92)' : 'rgba(255,255,255,0.35)'}`,
                      background: effectiveHiResEnabled ? 'rgba(76,58,16,0.92)' : 'rgba(10,16,28,0.88)',
                      color: effectiveHiResEnabled ? '#fff4cf' : '#eef2ff',
                      padding: '8px 12px',
                      fontSize: 11,
                      fontWeight: 800,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      cursor: hiResAvailable ? 'pointer' : 'default',
                      opacity: hiResAvailable ? 1 : 0.55,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    (HI RES)
                  </button>
                </div>
              </label>
            </div>
          </div>
        </header>

        {loading ? (
          <div className="merch-gallery2d-state">Laster merchandise-galleri...</div>
        ) : null}

        {!loading && error ? (
          <div className="merch-gallery2d-state merch-gallery2d-state--error">{error}</div>
        ) : null}

        {!loading && !error && activeImage ? (
          <section className="merch-gallery2d-stage">
            <button
              type="button"
              className="merch-gallery2d-nav merch-gallery2d-nav--prev"
              onClick={() => moveBy(-1)}
              aria-label="Previous image"
              title="Previous image"
            >
              ❮
            </button>

            <div
              className="merch-gallery2d-hero"
              style={{ cursor: zoomActiveForCurrentImage ? 'zoom-out' : 'zoom-in' }}
              {...getPressZoomHandlers(activeImage.id)}
            >
              <div className="merch-gallery2d-hero-gloss" aria-hidden />
              <div
                className={`merch-gallery2d-loader ${(imageReadyImageId === activeImage.id || activeImage.type === 'info' || activeImage.type === 'inventory') ? 'is-hidden' : ''}`}
                aria-hidden
              >
                <span className="merch-gallery2d-loader-smoke merch-gallery2d-loader-smoke--one" />
                <span className="merch-gallery2d-loader-smoke merch-gallery2d-loader-smoke--two" />
                <span className="merch-gallery2d-loader-smoke merch-gallery2d-loader-smoke--three" />
              </div>

              <div
                key={`item-smoke-${activeImage.id}-${transitionToken}`}
                className={`merch-gallery2d-info-smoke ${direction === 'next' ? 'slide-next' : 'slide-prev'}`}
                aria-hidden
              >
                <span className="merch-gallery2d-info-smoke-layer merch-gallery2d-info-smoke-layer--one" />
                <span className="merch-gallery2d-info-smoke-layer merch-gallery2d-info-smoke-layer--two" />
                <span className="merch-gallery2d-info-smoke-layer merch-gallery2d-info-smoke-layer--three" />
              </div>

              {activeImage.type === 'info' ? (
                <div
                  className="merch-gallery2d-image merch-gallery2d-image--base merch-gallery2d-image--info-card"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    height: '100%',
                    padding: 'clamp(16px, 8vh, 72px) 20px 32px',
                    textAlign: 'center',
                    background: 'transparent',
                    position: 'relative',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                    perspective: '1200px',
                  }}
                >
                  <div className="merch-gallery2d-info-text-3d" style={{ position: 'relative', zIndex: 4, opacity: 1, color: '#fff' }}>
                    <h2 style={{ fontSize: 'clamp(30px, 6.2vw, 48px)', margin: '0 0 16px 0', color: '#fff', opacity: 1, textShadow: '0 2px 4px rgba(0,0,0,0.8), 2px 4px 8px rgba(0,0,0,0.6), 4px 8px 16px rgba(0,0,0,0.4), 6px 12px 24px rgba(0,0,0,0.3)', fontWeight: 700 }}>{activeImage.title}</h2>
                    <p style={{ fontSize: 'clamp(16px, 3.6vw, 24px)', lineHeight: 1.5, opacity: 1, width: '100%', maxWidth: 820, color: '#fff', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                      {activeImage.description}
                    </p>
                  </div>
                </div>
              ) : activeImage.type === 'inventory' ? (
                <div
                  className="merch-gallery2d-image merch-gallery2d-image--base"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    alignItems: 'start',
                    justifyContent: 'center',
                    height: '100%',
                    padding: '26px 20px',
                    background: 'transparent',
                    gap: 18,
                  }}
                >
                  <div style={{ display: 'grid', gap: 14, width: '100%' }}>
                    <h2 style={{ fontSize: 22, margin: 0, textAlign: 'center', color: '#f8fbff', textShadow: '0 1px 2px rgba(0,0,0,0.45)' }}>{activeImage.title}</h2>
                    
                    {(() => {
                      const variantRows = Array.isArray(activeImage.variantStock) ? activeImage.variantStock : [];
                      const totalStockFromRows = variantRows.reduce((sum, row) => {
                        return sum + getAvailableStockCount(row);
                      }, 0);
                      const totalStock = variantRows.length ? totalStockFromRows : Math.max(0, Number(activeImage.stockCount) || 0);
                      const hasVariantGroups = Array.isArray(activeImage.variantGroups) && activeImage.variantGroups.length > 0;

                      return (
                        <div style={{ display: 'grid', gap: 14, width: '100%', minHeight: '100%', justifyContent: 'center', alignContent: 'center', alignItems: 'center' }}>
                          {!hasVariantGroups ? (() => {
                            const actionState = getInventoryActionState(totalStock);
                            const requestKey = 'inventory::standard';
                            const isPending = inventoryActionPendingKey === requestKey;
                            return (
                              <button
                                type='button'
                                onClick={() => {
                                  if (actionState.action === 'request') {
                                    openStockRequestModal([], requestKey);
                                    return;
                                  }
                                  addInventorySelectionToCart([]);
                                }}
                                disabled={isPending}
                                style={{
                                  minWidth: 220,
                                  maxWidth: '100%',
                                  justifySelf: 'center',
                                  textAlign: 'center',
                                  fontSize: 15,
                                  fontWeight: 700,
                                  letterSpacing: '0.08em',
                                  color: actionState.textColor,
                                  cursor: isPending ? 'wait' : 'pointer',
                                  padding: '12px 18px',
                                  border: `2px solid ${actionState.borderColor}`,
                                  borderRadius: 10,
                                  transition: 'transform 160ms ease, opacity 160ms ease',
                                  opacity: isPending ? 0.72 : 0.96,
                                  background: actionState.background,
                                }}
                              >
                                {isPending ? 'SENDING REQUEST...' : actionState.label}
                              </button>
                            );
                          })() : (() => {
                            const tableData = buildInventoryTableData(activeImage.variantGroups, activeImage.variantStock);
                            if (!tableData.rowLabels.length || !tableData.columnLabels.length) {
                              return null;
                            }
                            return (
                              <div style={{ overflowX: 'auto', display: 'flex', justifyContent: 'center' }}>
                                <table style={{ width: 'auto', maxWidth: '100%', borderCollapse: 'collapse', tableLayout: 'auto', margin: '0 auto' }}>
                                  <thead>
                                    <tr>
                                      <th style={{ textAlign: 'left', fontSize: 12, opacity: 0.85, borderBottom: '1px solid rgba(255,255,255,0.2)', padding: '8px 10px' }}>
                                        {tableData.rowHeader}
                                      </th>
                                      {tableData.columnLabels.map((columnLabel) => (
                                        <th key={columnLabel} style={{ textAlign: 'center', fontSize: 12, opacity: 0.85, borderBottom: '1px solid rgba(255,255,255,0.2)', padding: '8px 10px' }}>
                                          {columnLabel}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {tableData.rowLabels.map((rowLabel) => (
                                      <tr key={rowLabel}>
                                        <td style={{ textAlign: 'left', fontSize: 13, borderBottom: '1px solid rgba(255,255,255,0.1)', padding: '8px 10px' }}>{rowLabel}</td>
                                        {tableData.columnLabels.map((columnLabel) => {
                                          const stock = Number(tableData.lookup[`${rowLabel}::${columnLabel}`] || 0);
                                          const actionState = getInventoryActionState(stock);
                                          const selectedVariants = buildSelectedVariantsForInventoryCell(activeImage.variantGroups, rowLabel, tableData.columnLabels.length > 1 ? columnLabel : '');
                                          const requestKey = `${rowLabel}::${columnLabel}`;
                                          const isPending = inventoryActionPendingKey === requestKey;
                                          return (
                                            <td key={`${rowLabel}-${columnLabel}`} style={{ textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,0.1)', padding: '8px 10px' }}>
                                              <button
                                                type='button'
                                                onClick={() => {
                                                  if (actionState.action === 'request') {
                                                    openStockRequestModal(selectedVariants, requestKey);
                                                    return;
                                                  }
                                                  addInventorySelectionToCart(selectedVariants);
                                                }}
                                                disabled={isPending}
                                                style={{
                                                  width: '100%',
                                                  minWidth: 138,
                                                  padding: '9px 10px',
                                                  borderRadius: 9,
                                                  border: `2px solid ${actionState.borderColor}`,
                                                  background: actionState.background,
                                                  color: actionState.textColor,
                                                  fontSize: 12,
                                                  fontWeight: 700,
                                                  letterSpacing: '0.06em',
                                                  cursor: isPending ? 'wait' : 'pointer',
                                                  opacity: isPending ? 0.72 : 0.96,
                                                  transition: 'transform 160ms ease, opacity 160ms ease',
                                                }}
                                              >
                                                {isPending ? 'SENDING REQUEST...' : actionState.label}
                                              </button>
                                            </td>
                                          );
                                        })}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ) : (
                <>
                  <img
                    key={`main-${activeImage.id}-${transitionToken}`}
                    src={activeImageDisplaySrc}
                    alt={activeImage.title || meta?.title || 'Merchandise'}
                    onLoad={() => setImageReadyImageId(activeImage.id)}
                    onError={() => setImageReadyImageId(activeImage.id)}
                    className={`merch-gallery2d-image merch-gallery2d-image--base ${direction === 'next' ? 'slide-next' : 'slide-prev'}`}
                    style={{
                      opacity: imageReadyImageId === activeImage.id ? 1 : 0,
                      transform: mediaZoomTransform,
                      transformOrigin: mediaZoomOrigin,
                      transition: `opacity 360ms ease, transform ${GALLERY_PRESS_ZOOM_TRANSITION_MS}ms ease`,
                    }}
                    draggable={false}
                  />
                </>
              )}

              {zoomRipples
                .filter((entry) => entry.mediaId === activeImage.id)
                .map((entry) => (
                  <React.Fragment key={`zoom-ripple-${entry.id}`}>
                    <span
                      className="merch-gallery2d-ripple merch-gallery2d-ripple--one"
                      style={{ left: `${entry.x}%`, top: `${entry.y}%` }}
                      aria-hidden
                    />
                    <span
                      className="merch-gallery2d-ripple merch-gallery2d-ripple--two"
                      style={{ left: `${entry.x}%`, top: `${entry.y}%` }}
                      aria-hidden
                    />
                    <span
                      className="merch-gallery2d-ripple merch-gallery2d-ripple--three"
                      style={{ left: `${entry.x}%`, top: `${entry.y}%` }}
                      aria-hidden
                    />
                  </React.Fragment>
                ))}

              <div className="merch-gallery2d-counter-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <div className="merch-gallery2d-counter" aria-live="polite">
                  {activeImageIndexDisplay} / {images.length}
                </div>
              </div>
            </div>

            <button
              type="button"
              className="merch-gallery2d-nav merch-gallery2d-nav--next"
              onClick={() => moveBy(1)}
              aria-label="Next image"
              title="Next image"
            >
              ❯
            </button>
          </section>
        ) : null}

        {!loading && !error && images.length > 1 ? (
          <section className="merch-gallery2d-thumbs" aria-label="Bildeoversikt">
            {images.map((image, index) => (
              <button
                key={image.id}
                type="button"
                className={`merch-gallery2d-thumb ${index === activeIndex ? 'is-active' : ''}`}
                onClick={() => moveTo(index)}
                aria-label={`Vis bilde ${index + 1}`}
                title={image.title || `Bilde ${index + 1}`}
              >
                {image.type === 'info' ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%',
                      background: 'transparent',
                    }}
                  >
                    <div
                      style={{
                        width: '48px',
                        height: '48px',
                        border: '3px solid rgba(141, 255, 198, 0.8)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '36px',
                        fontWeight: 700,
                        color: 'transparent',
                        fontFamily: 'Georgia, serif',
                        WebkitTextStroke: '1.5px rgba(141, 255, 198, 0.8)',
                      }}
                    >
                      i
                    </div>
                  </div>
                ) : image.type === 'inventory' ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(135deg, rgba(30,35,40,0.8), rgba(8,8,9,0.95))',
                      color: '#fff',
                      fontSize: 14,
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                    }}
                  >
                    STOCK
                  </div>
                ) : (
                  <img
                    src={image.thumbSrc || image.mainSrc}
                    alt={image.title || `Merchandise bilde ${index + 1}`}
                    loading="lazy"
                    draggable={false}
                  />
                )}
              </button>
            ))}
          </section>
        ) : null}
      </div>

      <button
        type='button'
        className="merch-gallery2d-cart-trigger"
        onClick={() => setShowCart(true)}
      >
        DØDSEGO CART ({totalCartItems})
      </button>

      {cartFeedback ? (
        <div className="merch-gallery2d-feedback" role="status" aria-live="polite">
          {cartFeedback}
        </div>
      ) : null}

      {showBuyEntry && meta ? (
        <div
          onClick={() => setShowBuyEntry(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 1240, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{ width: 'min(760px, 94vw)', borderRadius: 14, border: '1px solid rgba(255,255,255,0.26)', background: 'linear-gradient(135deg, rgba(30,35,40,0.94), rgba(8,8,9,0.96))', color: '#fff', padding: 18, display: 'grid', gap: 14 }}
          >
            <div style={{ display: 'grid', gap: 6 }}>
              <div style={{ textTransform: 'uppercase', letterSpacing: '0.12em', fontSize: 12, opacity: 0.82, color: '#f8fbff', textAlign: 'center' }}>
                INVENTORY STATUS
              </div>
              <h3 style={{ margin: 0, textAlign: 'center', fontSize: 26 }}>{meta.title || 'Merchandise'}</h3>
              <div style={{ textAlign: 'center', opacity: 0.9 }}>{formatNok(meta.salePriceNok)}</div>
              {String(meta.blurb || meta.description || '').trim() ? (
                <div style={{ textAlign: 'center', opacity: 0.88, lineHeight: 1.4 }}>{String(meta.blurb || meta.description || '').trim()}</div>
              ) : null}
            </div>

            {!buyEntryVariantGroups.length ? (
              (() => {
                const totalStock = Math.max(0, Number(meta.totalAvailableStock) || 0);
                const actionState = getInventoryActionState(totalStock);
                const requestKey = `inventory::${String(meta.id || '')}::standard`;
                const isPending = inventoryActionPendingKey === requestKey;
                return (
                  <div style={{ display: 'grid', justifyItems: 'center', gap: 10 }}>
                    <button
                      type='button'
                      disabled={isPending}
                      onClick={async () => {
                        if (actionState.action === 'request') {
                          openStockRequestModal([], requestKey);
                          return;
                        }
                        addInventorySelectionToCart([]);
                        setShowBuyEntry(false);
                      }}
                      style={{ minWidth: 220, borderRadius: 10, border: `2px solid ${actionState.borderColor}`, background: actionState.background, color: actionState.textColor, padding: '12px 18px', fontWeight: 700, letterSpacing: '0.08em', cursor: isPending ? 'wait' : 'pointer', opacity: isPending ? 0.72 : 0.96 }}
                    >
                      {isPending ? 'SENDING REQUEST...' : actionState.label}
                    </button>
                  </div>
                );
              })()
            ) : (
              <div style={{ overflowX: 'auto', display: 'flex', justifyContent: 'center' }}>
                <table style={{ width: 'auto', maxWidth: '100%', borderCollapse: 'collapse', tableLayout: 'auto', margin: '0 auto' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', fontSize: 12, opacity: 0.85, borderBottom: '1px solid rgba(255,255,255,0.2)', padding: '8px 10px' }}>
                        {buyEntryInventoryTable.rowHeader}
                      </th>
                      {buyEntryInventoryTable.columnLabels.map((columnLabel) => (
                        <th key={columnLabel} style={{ textAlign: 'center', fontSize: 12, opacity: 0.85, borderBottom: '1px solid rgba(255,255,255,0.2)', padding: '8px 10px' }}>
                          {columnLabel}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {buyEntryInventoryTable.rowLabels.map((rowLabel) => (
                      <tr key={rowLabel}>
                        <td style={{ textAlign: 'left', fontSize: 13, borderBottom: '1px solid rgba(255,255,255,0.1)', padding: '8px 10px' }}>{rowLabel}</td>
                        {buyEntryInventoryTable.columnLabels.map((columnLabel) => {
                          const stock = Number(buyEntryInventoryTable.lookup[`${rowLabel}::${columnLabel}`] || 0);
                          const actionState = getInventoryActionState(stock);
                          const selectedVariants = buildSelectedVariantsForInventoryCell(buyEntryVariantGroups, rowLabel, buyEntryInventoryTable.columnLabels.length > 1 ? columnLabel : '');
                          const requestKey = `${String(meta.id || '')}::${rowLabel}::${columnLabel}`;
                          const isPending = inventoryActionPendingKey === requestKey;
                          return (
                            <td key={`${rowLabel}-${columnLabel}`} style={{ textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,0.1)', padding: '8px 10px' }}>
                              <button
                                type='button'
                                disabled={isPending}
                                onClick={async () => {
                                  if (actionState.action === 'request') {
                                    openStockRequestModal(selectedVariants, requestKey);
                                    return;
                                  }
                                  addInventorySelectionToCart(selectedVariants);
                                  setShowBuyEntry(false);
                                }}
                                style={{ width: '100%', minWidth: 138, padding: '9px 10px', borderRadius: 9, border: `2px solid ${actionState.borderColor}`, background: actionState.background, color: actionState.textColor, fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', cursor: isPending ? 'wait' : 'pointer', opacity: isPending ? 0.72 : 0.96 }}
                              >
                                {isPending ? 'SENDING REQUEST...' : actionState.label}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 8 }}>
              <button
                type='button'
                onClick={() => setShowBuyEntry(false)}
                style={{ borderRadius: 8, border: '1px solid #444', background: '#111', color: '#fff', padding: '8px 11px', cursor: 'pointer' }}
              >
                CLOSE
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <MerchandiseStockRequestModal
        open={stockRequestDraft.open}
        pending={stockRequestDraft.open && inventoryActionPendingKey === stockRequestDraft.requestKey}
        productTitle={meta?.title || ''}
        selectedVariants={stockRequestDraft.selectedVariants}
        onClose={() => setStockRequestDraft(EMPTY_STOCK_REQUEST_DRAFT)}
        onSubmit={submitStockRequestFromModal}
      />

      <MerchandiseCartModal
        open={showCart}
        cartItems={cartItems}
        setCartItems={setCartItems}
        shippingCostNok={Number(meta?.shippingCostNok || 75) || 75}
        shippingCostInternationalNok={Number(meta?.shippingCostInternationalNok || 150) || 150}
        onClose={() => setShowCart(false)}
        onOrderCompleted={() => navigate('/')}
      />
    </div>
  );
};

export default MerchandiseGallery2D;
